import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMetaPixel } from '@/hooks/useMetaPixel';
import { useTrafficSource } from '@/hooks/useTrafficSource';

/**
 * Provider component that tracks page views automatically
 * Compliant with Meta Health & Wellness - Other category policies
 * Does not send treatment-specific or health-related data
 */
export const MetaPixelProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { trackViewContent, isEnabled } = useMetaPixel();
  const trafficSource = useTrafficSource();

  // Track page views on route change with compliant parameters
  useEffect(() => {
    if (!isEnabled) return;

    // Determine content category from path - using generic categories only
    const path = location.pathname;
    let contentCategory = 'General';
    let contentName = 'Page View';

    // Landing pages - high priority tracking (generic names)
    if (path.includes('/landing/')) {
      contentCategory = 'Landing Page';
      contentName = 'Landing Page View';
    } else if (path.includes('/treatments/')) {
      contentCategory = 'Services'; // Generic, not "Treatment" or "Dental"
      contentName = 'Service Page View';
    } else if (path.includes('/contact')) {
      contentCategory = 'Contact';
      contentName = 'Contact Page';
    } else if (path.includes('/gallery')) {
      contentCategory = 'Gallery';
      contentName = 'Gallery Page';
    } else if (path.includes('/about')) {
      contentCategory = 'About';
      contentName = 'About Page';
    } else if (path.includes('/blog')) {
      contentCategory = 'Blog';
      contentName = 'Blog Page';
    }

    trackViewContent({
      content_name: contentName,
      content_category: contentCategory,
      content_type: 'page',
      language: i18n.language,
      traffic_source: trafficSource.traffic_source,
      traffic_medium: trafficSource.traffic_medium,
      traffic_campaign: trafficSource.traffic_campaign,
      page_path: path,
      referrer: document.referrer,
    });
  }, [location.pathname, isEnabled, trackViewContent, i18n.language, trafficSource]);

  return <>{children}</>;
};

export default MetaPixelProvider;
